import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import './MiniMapPlayer.css';
import { buildingSizeTier } from './sc2/replayUnitLookup';
import {
  cameraAtTime,
  cameraViewportTrapezoid,
  colorForPlayer,
  entitiesAtTime,
  formatClock,
  rosterForPlayer,
} from '../replayPlayerState';

const RESOURCE_COLORS = {
  mineral: '#4fc3f7',
  gas: '#66bb6a',
};

const BUILDING_TIER_SCALE = { s: 1.0, m: 1.65, l: 2.45 };
const DRAW_ORDER = { mineral: 0, gas: 1, building: 2, worker: 3, army: 4 };

function drawDiamond(ctx, px, py, r) {
  ctx.beginPath();
  ctx.moveTo(px, py - r);
  ctx.lineTo(px + r, py);
  ctx.lineTo(px, py + r);
  ctx.lineTo(px - r, py);
  ctx.closePath();
  ctx.fill();
  ctx.stroke();
}

function CompositionList({ title, items, emptyLabel }) {
  return (
    <div className="minimap-comp-block">
      <div className="minimap-comp-title">{title}</div>
      {items.length === 0 ? (
        <div className="minimap-comp-empty">{emptyLabel}</div>
      ) : (
        <ul className="minimap-comp-list">
          {items.map((item) => (
            <li key={item.name}>
              <span className="minimap-comp-name">{item.name}</span>
              <span className="minimap-comp-count">×{item.count}</span>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

export function PlayerRosterCard({ roster }) {
  const { player, cost, army, buildings, workers } = roster;
  return (
    <div className="minimap-roster-card" data-testid={`roster-${player.pid}`}>
      <div className="minimap-roster-header">
        <span
          className="minimap-swatch"
          style={{ backgroundColor: colorForPlayer(player) }}
        />
        <strong>{player.name}</strong>
        <span className="minimap-race">{player.race}</span>
      </div>
      <div className="minimap-army-cost" data-testid={`army-cost-${player.pid}`}>
        Army cost:{' '}
        <span className="minimap-minerals">{cost.minerals}</span>
        {' / '}
        <span className="minimap-gas">{cost.gas}</span>
        <span className="minimap-army-total"> (Σ {cost.total})</span>
      </div>
      <div className="minimap-worker-line">Workers {workers}</div>
      <CompositionList title="Units" items={army} emptyLabel="No army units" />
      <CompositionList
        title="Buildings"
        items={buildings}
        emptyLabel="No buildings"
      />
    </div>
  );
}

const MiniMapPlayer = ({
  minimap,
  time: controlledTime,
  playing: controlledPlaying,
  speed: controlledSpeed,
  onTimeChange,
  onPlayingChange,
  onSpeedChange,
  hideControls = false,
  compact = false,
  cameras = null,
  cameraPid = null,
  canvasSize,
}) => {
  const canvasRef = useRef(null);
  const [internalTime, setInternalTime] = useState(0);
  const [internalPlaying, setInternalPlaying] = useState(false);
  const [internalSpeed, setInternalSpeed] = useState(8);
  const [selectedPid, setSelectedPid] = useState('all');

  const controlled = typeof onTimeChange === 'function';
  const time = controlled ? (controlledTime ?? 0) : internalTime;
  const playing = controlled ? Boolean(controlledPlaying) : internalPlaying;
  const speed = controlled ? (controlledSpeed ?? 8) : internalSpeed;
  const size = canvasSize || (compact ? 220 : 420);

  const setTime = (valueOrFn) => {
    const next = typeof valueOrFn === 'function' ? valueOrFn(time) : valueOrFn;
    if (controlled) onTimeChange(next);
    else setInternalTime(next);
  };
  const setPlaying = (valueOrFn) => {
    const next = typeof valueOrFn === 'function' ? valueOrFn(playing) : valueOrFn;
    if (controlled) onPlayingChange?.(next);
    else setInternalPlaying(next);
  };
  const setSpeed = (value) => {
    if (controlled) onSpeedChange?.(value);
    else setInternalSpeed(value);
  };

  const duration = minimap?.duration || 0;
  const players = minimap?.players || [];
  const playersByPid = useMemo(() => {
    const map = new Map();
    players.forEach((p) => map.set(p.pid, p));
    return map;
  }, [players]);

  const events = minimap?.events || [];

  const entities = useMemo(
    () => entitiesAtTime(events, time),
    [events, time]
  );

  const counts = useMemo(() => {
    const result = { building: 0, worker: 0, army: 0, mineral: 0, gas: 0 };
    entities.forEach((e) => {
      if (result[e.kind] != null) result[e.kind] += 1;
    });
    return result;
  }, [entities]);

  const rosters = useMemo(() => {
    const scopedPlayers =
      selectedPid === 'all'
        ? players
        : players.filter((p) => p.pid === selectedPid);
    return scopedPlayers.map((player) => rosterForPlayer(entities, player));
  }, [entities, players, selectedPid]);

  const camera = useMemo(
    () => cameraAtTime(cameras, cameraPid, time),
    [cameras, cameraPid, time]
  );

  const draw = useCallback(() => {
    const canvas = canvasRef.current;
    if (!canvas || !minimap) return;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;
    const { width: mapW, height: mapH } = minimap;
    const scaleX = size / mapW;
    const scaleY = size / mapH;
    const cell = Math.min(scaleX, scaleY);

    ctx.clearRect(0, 0, size, size);

    const gradient = ctx.createLinearGradient(0, 0, size, size);
    gradient.addColorStop(0, '#1a2332');
    gradient.addColorStop(1, '#0f1620');
    ctx.fillStyle = gradient;
    ctx.fillRect(0, 0, size, size);

    ctx.strokeStyle = 'rgba(255,255,255,0.04)';
    ctx.lineWidth = 1;
    const step = Math.max(8, Math.floor(size / 16));
    for (let i = 0; i <= size; i += step) {
      ctx.beginPath();
      ctx.moveTo(i, 0);
      ctx.lineTo(i, size);
      ctx.stroke();
      ctx.beginPath();
      ctx.moveTo(0, i);
      ctx.lineTo(size, i);
      ctx.stroke();
    }

    const ordered = [...entities].sort(
      (a, b) => (DRAW_ORDER[a.kind] ?? 9) - (DRAW_ORDER[b.kind] ?? 9)
    );

    for (const entity of ordered) {
      if (entity.x == null || entity.y == null) continue;
      const px = entity.x * scaleX;
      const py = size - entity.y * scaleY;
      ctx.lineWidth = 1;

      if (entity.kind === 'mineral' || entity.kind === 'gas') {
        const r = Math.max(1.8, cell * 0.45);
        ctx.fillStyle = RESOURCE_COLORS[entity.kind];
        ctx.strokeStyle = 'rgba(0,0,0,0.35)';
        ctx.beginPath();
        ctx.arc(px, py, r, 0, Math.PI * 2);
        ctx.fill();
        ctx.stroke();
        continue;
      }

      const color = colorForPlayer(playersByPid.get(entity.pid));
      ctx.fillStyle = color;
      ctx.strokeStyle = 'rgba(0,0,0,0.45)';

      if (entity.kind === 'building') {
        const tier = buildingSizeTier(entity.name);
        const s = Math.max(3, cell * BUILDING_TIER_SCALE[tier]);
        ctx.fillRect(px - s / 2, py - s / 2, s, s);
        ctx.strokeRect(px - s / 2, py - s / 2, s, s);
      } else if (entity.kind === 'worker') {
        const r = Math.max(1.5, cell * 0.55);
        ctx.beginPath();
        ctx.arc(px, py, r, 0, Math.PI * 2);
        ctx.fill();
      } else {
        const r = Math.max(2.2, cell * 0.9);
        drawDiamond(ctx, px, py, r);
      }
    }

    if (camera && camera.x != null && camera.y != null) {
      // SC2 FoV on the minimap is a trapezoid (far edge wider), not a rectangle.
      const corners = cameraViewportTrapezoid({
        mapW,
        mapH,
        size,
        x: camera.x,
        y: camera.y,
      });
      const camPlayer = playersByPid.get(camera.pid ?? cameraPid);
      const stroke = colorForPlayer(camPlayer);
      ctx.beginPath();
      ctx.moveTo(corners[0].x, corners[0].y);
      for (let i = 1; i < corners.length; i += 1) {
        ctx.lineTo(corners[i].x, corners[i].y);
      }
      ctx.closePath();
      ctx.fillStyle = 'rgba(255, 255, 255, 0.06)';
      ctx.fill();
      ctx.strokeStyle = stroke;
      ctx.lineWidth = 2;
      ctx.stroke();
    }
  }, [entities, minimap, playersByPid, size, camera, cameraPid]);

  useEffect(() => {
    draw();
  }, [draw]);

  useEffect(() => {
    if (controlled || !playing || duration <= 0) return undefined;
    let raf = 0;
    let last = performance.now();
    const tick = (now) => {
      const dt = (now - last) / 1000;
      last = now;
      setTime((prev) => {
        const next = prev + dt * speed;
        if (next >= duration) {
          setPlaying(false);
          return duration;
        }
        return next;
      });
      raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [controlled, playing, speed, duration]);

  useEffect(() => {
    if (!controlled) {
      setInternalTime(0);
      setInternalPlaying(false);
    }
    setSelectedPid('all');
  }, [minimap, controlled]);

  if (!minimap || !events.length) {
    return (
      <div className="minimap-player empty">
        No minimap data in this parsed replay. Force re-parse to generate it.
      </div>
    );
  }

  return (
    <div className={`minimap-player${compact ? ' compact' : ''}`}>
      <div className="minimap-stage">
        <canvas
          ref={canvasRef}
          className="minimap-canvas"
          width={size}
          height={size}
          aria-label="Replay minimap"
        />
        {!compact && (
          <div className="minimap-legend">
            {players.map((player) => (
              <div key={player.pid} className="minimap-legend-item">
                <span
                  className="minimap-swatch"
                  style={{ backgroundColor: colorForPlayer(player) }}
                />
                <span>{player.name}</span>
                <span className="minimap-race">{player.race}</span>
              </div>
            ))}
            <div className="minimap-legend-item muted">
              <span className="minimap-swatch diamond" />
              <span>Army</span>
            </div>
            <div className="minimap-legend-item muted">
              <span className="minimap-swatch square" />
              <span>Building</span>
            </div>
            <div className="minimap-legend-item muted">
              <span
                className="minimap-swatch circle"
                style={{ backgroundColor: RESOURCE_COLORS.mineral }}
              />
              <span>Minerals</span>
            </div>
            <div className="minimap-legend-item muted">
              <span
                className="minimap-swatch circle"
                style={{ backgroundColor: RESOURCE_COLORS.gas }}
              />
              <span>Gas</span>
            </div>
            {cameras && cameras.length > 0 && (
              <div className="minimap-legend-item muted">
                <span className="minimap-swatch camera-box" aria-hidden />
                <span>Camera</span>
              </div>
            )}
          </div>
        )}
      </div>

      {!hideControls && (
        <>
          <div className="minimap-controls">
            <button
              type="button"
              className="minimap-btn"
              onClick={() => setPlaying((p) => !p)}
            >
              {playing ? 'Pause' : 'Play'}
            </button>
            <button
              type="button"
              className="minimap-btn secondary"
              onClick={() => {
                setPlaying(false);
                setTime(0);
              }}
            >
              Reset
            </button>
            <label className="minimap-speed">
              Speed
              <select value={speed} onChange={(e) => setSpeed(Number(e.target.value))}>
                <option value={1}>1x</option>
                <option value={4}>4x</option>
                <option value={8}>8x</option>
                <option value={16}>16x</option>
                <option value={32}>32x</option>
              </select>
            </label>
            <span className="minimap-clock">
              {formatClock(time)} / {formatClock(duration)}
            </span>
          </div>

          <input
            className="minimap-scrubber"
            type="range"
            min={0}
            max={duration || 1}
            step={0.1}
            value={Math.min(time, duration || 0)}
            onChange={(e) => {
              setPlaying(false);
              setTime(Number(e.target.value));
            }}
          />
        </>
      )}

      {!compact && (
        <>
          <div className="minimap-stats">
            <span>Buildings {counts.building}</span>
            <span>Workers {counts.worker}</span>
            <span>Army {counts.army}</span>
            <span>Minerals {counts.mineral}</span>
            <span>Gas {counts.gas}</span>
            <span>Alive {entities.length}</span>
          </div>

          <div className="minimap-toggle" role="tablist" aria-label="Player roster">
            <button
              type="button"
              role="tab"
              aria-selected={selectedPid === 'all'}
              className={`minimap-toggle-btn${selectedPid === 'all' ? ' active' : ''}`}
              onClick={() => setSelectedPid('all')}
            >
              All
            </button>
            {players.map((player) => (
              <button
                key={player.pid}
                type="button"
                role="tab"
                aria-selected={selectedPid === player.pid}
                className={`minimap-toggle-btn${selectedPid === player.pid ? ' active' : ''}`}
                onClick={() => setSelectedPid(player.pid)}
              >
                {player.name}
              </button>
            ))}
          </div>

          <div className="minimap-roster">
            {rosters.map((roster) => (
              <PlayerRosterCard key={roster.player.pid} roster={roster} />
            ))}
          </div>
        </>
      )}
    </div>
  );
};

export default MiniMapPlayer;
